// packages/report-components/src/drawingTokens.ts
//
// The report surface's half of the drawing vocabulary (plan 2026-09-25, D2 and
// D5): a scorecard's `border` and a shape block's `drawing`, as closed sets, and
// the one place a colour TOKEN becomes CSS on a report.
//
// ── Why every reader coerces ────────────────────────────────────────────
// A stored border or shape is UNTRUSTED here. It reaches this package as JSON
// (a compiled JSX literal, an outline's `_facts`, a block DTO's `spec_json`),
// and a TypeScript type validates none of it. Anything outside the sets falls
// back to `null`, which every caller reads as "draw today's default". A colour
// is resolved by map LOOKUP and never by interpolating the input into CSS: a
// stored `"red;background:url(x)"` must not reach a style attribute.
//
// ── Two surfaces, two grammars (ADR-0380) ───────────────────────────────
// A REPORT colour is a token. A DASHBOARD colour is a token, a theme slot
// `palette_1..8` or a lowercase `#rrggbb`. Each reader is named per surface
// (`coerceBorder` / `coerceDashboardBorder`, ...): a report reader drops a
// slot or a hex exactly as it drops any other value outside its set, so
// nothing but a token ever reaches a report style.
//
// The sets mirror the backend's (`ReportShapeSpec` / `DashboardShapeSpec`,
// emitted under `drawing` in the layout spec). They are written out here because this
// package draws in the email bundle and the sandbox without the frontend's
// generated files; a new member on one side and not the other falls back to
// the default drawing, never to an unstyled one.

/** Canonical order, the order a coerced `sides` is returned in. */
export const DRAWING_SIDES = ["top", "right", "bottom", "left"] as const;
export type DrawingSide = (typeof DRAWING_SIDES)[number];

/** A border eats into the scorecard's own box, and the strip pays it out of
 *  its 10px padding, so the set stops at 4. */
export const BORDER_THICKNESSES_PX = [1, 2, 3, 4] as const;
export type BorderThicknessPx = (typeof BORDER_THICKNESSES_PX)[number];

/** 8px is the ceiling: a horizontal line lives in a 30px row. */
export const LINE_THICKNESSES_PX = [1, 2, 3, 4, 6, 8] as const;
export type LineThicknessPx = (typeof LINE_THICKNESSES_PX)[number];

export const DRAWING_COLORS = ["rule", "muted", "text", "primary"] as const;
export type DrawingColor = (typeof DRAWING_COLORS)[number];

/** The dashboard's theme slots (ADR-0380, ADR-0351's slot vocabulary): slot N
 *  of the dashboard's 8 theme colours. */
export const PALETTE_SLOTS = [
  "palette_1",
  "palette_2",
  "palette_3",
  "palette_4",
  "palette_5",
  "palette_6",
  "palette_7",
  "palette_8",
] as const;
export type PaletteSlot = (typeof PALETTE_SLOTS)[number];
/** A custom colour as stored: lowercase `#rrggbb`, checked on every read. */
export type CustomColor = `#${string}`;
/** What a dashboard line, border or outline may store. */
export type DashboardColor = DrawingColor | PaletteSlot | CustomColor;

const CUSTOM_COLOR_RE = /^#[0-9a-f]{6}$/;

/** A slot is matched exactly, never with `in` against a map: a stored
 *  `"constructor"` must not resolve to a prototype member. */
export function isPaletteSlot(value: unknown): value is PaletteSlot {
  return typeof value === "string" && (PALETTE_SLOTS as readonly string[]).includes(value);
}

/** The slot's index into the 8 theme colours (0..7), or null. */
export function paletteSlotIndex(value: unknown): number | null {
  return isPaletteSlot(value) ? Number(value.slice("palette_".length)) - 1 : null;
}

/** A stored custom colour: exactly lowercase `#rrggbb`. The backend stores
 *  that spelling; anything else (`#FFF`, a name, `var()`, a trailing
 *  newline) is not one. */
export function isCustomColor(value: unknown): value is CustomColor {
  return typeof value === "string" && CUSTOM_COLOR_RE.test(value);
}

function dashboardColorOf<T extends string>(tokens: readonly T[]) {
  return (value: unknown): value is T | PaletteSlot | CustomColor =>
    isMember(tokens, value) || isPaletteSlot(value) || isCustomColor(value);
}

export const LINE_ORIENTATIONS = ["horizontal", "vertical"] as const;
export type LineOrientation = (typeof LINE_ORIENTATIONS)[number];

/** `sides: []` is "no border", a legal and different value from `null`
 *  ("today's default"). Thickness and colour are meaningless without a side,
 *  so they are optional in the stored form and defaulted on coercion. */
export type BorderSpec<C extends string = DrawingColor> = {
  sides: DrawingSide[];
  thickness_px: BorderThicknessPx;
  color: C;
};

export type ShapeSpec<C extends string = DrawingColor> = {
  kind: "line";
  orientation: LineOrientation;
  thickness_px: LineThicknessPx;
  color: C;
};

export type DashboardBorderSpec = BorderSpec<DashboardColor>;
export type DashboardShapeSpec = ShapeSpec<DashboardColor>;

/** Theme tokens → CSS. Each is a variable a brand can retint, with a print-safe
 *  fallback; `rule` is the strip's own hairline value (`DataPoint.tsx`). */
const REPORT_DRAWING_COLORS: Readonly<Record<DrawingColor, string>> = {
  rule: "var(--verity-report-rule, rgba(17, 24, 39, 0.22))",
  muted: "var(--verity-color-text-muted, #6b7280)",
  text: "var(--verity-color-text, #111827)",
  primary: "var(--verity-color-primary, #3455D3)",
};

function isMember<T>(set: readonly T[], value: unknown): value is T {
  return (set as readonly unknown[]).includes(value);
}

/** The CSS colour of a token on a report. An unknown token draws the rule. */
export function reportDrawingColor(token: unknown): string {
  return isMember(DRAWING_COLORS, token)
    ? REPORT_DRAWING_COLORS[token]
    : REPORT_DRAWING_COLORS.rule;
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === "object" && raw !== null && !Array.isArray(raw);
}

/** A stored border, or `null` (today's drawing) for anything outside the sets.
 *
 *  One bad field rejects the whole border rather than being dropped on its
 *  own: a border that silently lost its colour would draw something the author
 *  never chose, whereas the default is a drawing they have seen. */
export function coerceBorder(raw: unknown): BorderSpec | null {
  return coerceBorderWith(raw, (c): c is DrawingColor => isMember(DRAWING_COLORS, c));
}

/** A stored DASHBOARD border: the report's rule, with the dashboard colour
 *  grammar (ADR-0380). */
export function coerceDashboardBorder(raw: unknown): DashboardBorderSpec | null {
  return coerceBorderWith(raw, dashboardColorOf(DRAWING_COLORS));
}

function coerceBorderWith<C extends string>(raw: unknown, isColor: (c: unknown) => c is C): BorderSpec<C> | null {
  if (!isRecord(raw) || !Array.isArray(raw.sides)) return null;
  if (!raw.sides.every((side) => isMember(DRAWING_SIDES, side))) return null;
  const thickness = raw.thickness_px ?? 1;
  const color = raw.color ?? "rule";
  if (!isMember(BORDER_THICKNESSES_PX, thickness) || !isColor(color)) return null;
  const present = new Set<unknown>(raw.sides);
  return {
    sides: DRAWING_SIDES.filter((side) => present.has(side)),
    thickness_px: thickness,
    color,
  };
}

/** A stored shape, or `null` for anything outside the sets. */
export function coerceShape(raw: unknown): ShapeSpec | null {
  return coerceShapeWith(raw, (c): c is DrawingColor => isMember(DRAWING_COLORS, c));
}

/** A stored DASHBOARD line (ADR-0380). */
export function coerceDashboardShape(raw: unknown): DashboardShapeSpec | null {
  return coerceShapeWith(raw, dashboardColorOf(DRAWING_COLORS));
}

function coerceShapeWith<C extends string>(raw: unknown, isColor: (c: unknown) => c is C): ShapeSpec<C> | null {
  if (!isRecord(raw) || raw.kind !== "line") return null;
  const { orientation, thickness_px: thickness, color } = raw;
  if (
    !isMember(LINE_ORIENTATIONS, orientation) ||
    !isMember(LINE_THICKNESSES_PX, thickness) ||
    !isColor(color)
  ) {
    return null;
  }
  return { kind: "line", orientation, thickness_px: thickness, color };
}

// ── The backdrop: figures, arrows and callouts UNDER the grid ───────────────
//
// Plan 2026-10-09-dashboard-shapes-arrows-and-callouts (D2). A backdrop is the
// `{"shapes": [...]}` of a dashboard tab or a report page, array order =
// stacking order. Every set below mirrors `drawing.py` and is pinned to the
// emitted `drawing.backdrop` by a drift test, for the same reason the line
// vocabulary above is written out rather than imported.

export const BACKDROP_KINDS = ["figure", "arrow", "callout"] as const;
export type BackdropKind = (typeof BACKDROP_KINDS)[number];

export const BACKDROP_FIGURES = [
  "rectangle",
  "circle",
  "triangle",
  "heart",
  "diamond",
  "pentagon",
  "star",
  "hexagon",
  "octagon",
  "cylinder",
] as const;
export type BackdropFigure = (typeof BACKDROP_FIGURES)[number];

/** The figures that stretch with the blocks they sit behind when the stored
 *  shape says nothing (a panel); every other figure keeps its proportions. */
export const STRETCHING_FIGURES = ["rectangle"] as const;

/** A rectangle's corner in screen px (plan 2026-10-11): 0 is square. The
 *  drawing clamps it to half the shorter side. */
export const CORNER_RADIUS_MAX_PX = 48;
/** The figure that used to be a separate "Rounded rectangle", read as a
 *  rectangle with the corner it always drew (as `drawing.py` reads it). */
const LEGACY_ROUNDED_RECTANGLE = "rounded_rectangle";
const LEGACY_ROUNDED_CORNER_PX = 12;

export const ARROW_DIRECTIONS = ["right", "left", "up", "down"] as const;
export type ArrowDirection = (typeof ARROW_DIRECTIONS)[number];

export const ARROW_STYLES = ["line", "block"] as const;
export type ArrowStyle = (typeof ARROW_STYLES)[number];

export const CALLOUT_TAILS = [
  "bottom_left",
  "bottom_right",
  "top_left",
  "top_right",
  "left",
  "right",
  "none",
] as const;
export type CalloutTail = (typeof CALLOUT_TAILS)[number];

export const CALLOUT_TEXT_SIZES = ["sm", "md", "lg"] as const;
export type CalloutTextSize = (typeof CALLOUT_TEXT_SIZES)[number];

export const FILL_TOKENS = ["none", "subtle", "primary_subtle", "primary", "surface"] as const;
export type FillToken = (typeof FILL_TOKENS)[number];
/** What a dashboard backdrop fill may store (ADR-0380). */
export type DashboardFill = FillToken | PaletteSlot | CustomColor;

/** A figure's or callout's outline; 0 is "no outline", a legal look. */
export const OUTLINE_PX = [0, 1, 2, 3, 4] as const;
export type OutlinePx = (typeof OUTLINE_PX)[number];

/** An arrow is all outline, so it has no 0. */
export const ARROW_PX = [1, 2, 3, 4] as const;
export type ArrowPx = (typeof ARROW_PX)[number];

export const BACKDROP_MAX_SHAPES = 50;
export const CALLOUT_MAX_CHARS = 280;
const BACKDROP_COLUMNS = 12;
const BACKDROP_MAX_Y = 10_000;
const BACKDROP_MAX_H = 200;
const BACKDROP_ID_RE = /^[a-z0-9]{12}$/;

/** Where a shape sits, in GRID UNITS as decimals: x and w in columns of the
 *  12-column grid, y and h in rows. */
export type BackdropPlacement = { x: number; y: number; w: number; h: number };

/** The outline's dash (plan 2026-10-11). */
export const STROKE_STYLES = ["solid", "dashed", "dotted"] as const;
export type StrokeStyle = (typeof STROKE_STYLES)[number];

/** Every kind's finish: how much of it shows (100 opaque, 0 invisible), the
 *  outline's dash and a shadow along the outline. Absent keys read as these
 *  defaults, the look every shape had before them. */
export type ShapeFinish = { opacity_pct: number; stroke_style: StrokeStyle; shadow: boolean };

export type FigureShape<C extends string = DrawingColor, F extends string = FillToken> = ShapeFinish & {
  id: string;
  kind: "figure";
  placement: BackdropPlacement;
  figure: BackdropFigure;
  stroke_px: OutlinePx;
  color: C;
  fill: F;
  keep_proportions: boolean;
  /** Drawn on a rectangle only; kept on any other figure. */
  corner_radius_px: number;
};

export type ArrowShape<C extends string = DrawingColor, F extends string = FillToken> = ShapeFinish & {
  id: string;
  kind: "arrow";
  placement: BackdropPlacement;
  direction: ArrowDirection;
  style: ArrowStyle;
  stroke_px: ArrowPx;
  color: C;
  fill: F;
};

export type CalloutShape<C extends string = DrawingColor, F extends string = FillToken> = ShapeFinish & {
  id: string;
  kind: "callout";
  placement: BackdropPlacement;
  tail: CalloutTail;
  stroke_px: OutlinePx;
  color: C;
  fill: F;
  text: string;
  text_size: CalloutTextSize;
};

export type BackdropShape<C extends string = DrawingColor, F extends string = FillToken> =
  | FigureShape<C, F>
  | ArrowShape<C, F>
  | CalloutShape<C, F>;
export type DashboardBackdropShape = BackdropShape<DashboardColor, DashboardFill>;

/** Fill tokens → CSS. `none` is transparent; the two tints are mixed from a
 *  variable a brand can retint, so they stay light on white paper AND follow a
 *  white-label primary; `surface` is the card surface, so a callout reads as a
 *  card lying on the shapes behind it. A tint is mixed with the surface, not
 *  with transparent: a fill is opaque, so only the shape's opacity setting
 *  lets what lies behind it through. Chromium (the reading room and the PDF)
 *  draws `color-mix`; the mail never receives a backdrop. */
const REPORT_FILL_COLORS: Readonly<Record<FillToken, string>> = {
  none: "transparent",
  subtle: "color-mix(in srgb, var(--verity-color-text, #111827) 6%, var(--verity-color-surface, #ffffff))",
  primary_subtle: "color-mix(in srgb, var(--verity-color-primary, #3455D3) 12%, var(--verity-color-surface, #ffffff))",
  primary: "var(--verity-color-primary, #3455D3)",
  surface: "var(--verity-color-surface, #ffffff)",
};

/** The CSS fill of a token on a report. An unknown token fills nothing: a
 *  shape that cannot say its fill draws as an outline rather than as a block of
 *  a colour nobody chose. */
export function reportFillColor(token: unknown): string {
  return isMember(FILL_TOKENS, token) ? REPORT_FILL_COLORS[token] : REPORT_FILL_COLORS.none;
}

function finite(raw: unknown): raw is number {
  return typeof raw === "number" && Number.isFinite(raw);
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

/** The placement rules of `BackdropPlacement` in `drawing.py`: inside the 12
 *  columns, a width and a height, finite (a stored `Infinity` cannot arrive as
 *  JSON, but a compiled literal is not JSON). The page bound is a WRITE rule
 *  there and is deliberately absent here: the page box clips. */
function coercePlacement(raw: unknown): BackdropPlacement | null {
  if (!isRecord(raw)) return null;
  const { x, y, w, h } = raw;
  if (!finite(x) || !finite(y) || !finite(w) || !finite(h)) return null;
  const p = { x: round2(x), y: round2(y), w: round2(w), h: round2(h) };
  if (p.x < 0 || p.x > BACKDROP_COLUMNS) return null;
  if (p.y < 0 || p.y > BACKDROP_MAX_Y) return null;
  if (p.w <= 0 || p.w > BACKDROP_COLUMNS) return null;
  if (p.h <= 0 || p.h > BACKDROP_MAX_H) return null;
  if (round2(p.x + p.w) > BACKDROP_COLUMNS) return null;
  return p;
}

/** The value of `key`, or the backend's default when the key is ABSENT. A
 *  present value outside the set rejects the shape: the backend refuses it too
 *  (strict models), so a reader that coerced it would draw what no writer can. */
function member<T>(raw: Record<string, unknown>, key: string, set: readonly T[], fallback: T): T | null {
  const value = key in raw ? raw[key] : fallback;
  return isMember(set, value) ? value : null;
}

/** `member` for a colour, whose grammar is the surface's. */
function colorMember<T extends string>(
  raw: Record<string, unknown>,
  key: string,
  isColor: (c: unknown) => c is T,
  fallback: T,
): T | null {
  const value = key in raw ? raw[key] : fallback;
  return isColor(value) ? value : null;
}

type ColorGrammar<C extends string, F extends string> = {
  color: (c: unknown) => c is C;
  fill: (c: unknown) => c is F;
};

const REPORT_GRAMMAR: ColorGrammar<DrawingColor, FillToken> = {
  color: (c): c is DrawingColor => isMember(DRAWING_COLORS, c),
  fill: (c): c is FillToken => isMember(FILL_TOKENS, c),
};

const DASHBOARD_GRAMMAR: ColorGrammar<DashboardColor, DashboardFill> = {
  color: dashboardColorOf(DRAWING_COLORS),
  fill: dashboardColorOf(FILL_TOKENS),
};

/** The same characters `_CALLOUT_STRIP_RE` strips on write: control characters
 *  (newline and tab kept), bidi overrides/isolates and zero-width characters,
 *  any of which would let a callout visually rewrite the figure next to it.
 *  Stripped again here because a reader never trusts the writer. */
const CALLOUT_STRIP_RE =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g;

function coerceFinish(raw: Record<string, unknown>): ShapeFinish | null {
  const opacity = "opacity_pct" in raw ? raw.opacity_pct : 100;
  if (!Number.isInteger(opacity) || (opacity as number) < 0 || (opacity as number) > 100) return null;
  const strokeStyle = member(raw, "stroke_style", STROKE_STYLES, "solid");
  const shadow = "shadow" in raw ? raw.shadow : false;
  if (strokeStyle === null || typeof shadow !== "boolean") return null;
  return { opacity_pct: opacity as number, stroke_style: strokeStyle, shadow };
}

function coerceOneShape<C extends string, F extends string>(
  raw: unknown,
  grammar: ColorGrammar<C, F>,
): BackdropShape<C, F> | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.id !== "string" || !BACKDROP_ID_RE.test(raw.id)) return null;
  const placement = coercePlacement(raw.placement);
  if (!placement) return null;
  const { id } = raw;
  const finish = coerceFinish(raw);
  if (!finish) return null;
  if (raw.kind === "figure") {
    if (raw.figure === LEGACY_ROUNDED_RECTANGLE) {
      return coerceOneShape({ corner_radius_px: LEGACY_ROUNDED_CORNER_PX, ...raw, figure: "rectangle" }, grammar);
    }
    const figure = member(raw, "figure", BACKDROP_FIGURES, "rectangle");
    const radius = "corner_radius_px" in raw ? raw.corner_radius_px : 0;
    if (!Number.isInteger(radius) || (radius as number) < 0 || (radius as number) > CORNER_RADIUS_MAX_PX) {
      return null;
    }
    const stroke = member(raw, "stroke_px", OUTLINE_PX, 0);
    const color = colorMember(raw, "color", grammar.color, "rule" as C);
    const fill = colorMember(raw, "fill", grammar.fill, "subtle" as F);
    if (figure === null || stroke === null || color === null || fill === null) return null;
    const keep =
      "keep_proportions" in raw
        ? raw.keep_proportions
        : !isMember(STRETCHING_FIGURES, figure);
    if (typeof keep !== "boolean") return null;
    return {
      id,
      kind: "figure",
      placement,
      figure,
      stroke_px: stroke,
      color,
      fill,
      keep_proportions: keep,
      corner_radius_px: radius as number,
      ...finish,
    };
  }
  if (raw.kind === "arrow") {
    const direction = member(raw, "direction", ARROW_DIRECTIONS, "right");
    const style = member(raw, "style", ARROW_STYLES, "line");
    const stroke = member(raw, "stroke_px", ARROW_PX, 2);
    const color = colorMember(raw, "color", grammar.color, "text" as C);
    const fill = colorMember(raw, "fill", grammar.fill, "none" as F);
    if (direction === null || style === null || stroke === null || color === null || fill === null) {
      return null;
    }
    // One spelling per look, as on write: a line arrow has no body to fill.
    return {
      id,
      kind: "arrow",
      placement,
      direction,
      style,
      stroke_px: stroke,
      color,
      fill: style === "line" ? ("none" as F) : fill,
      ...finish,
    };
  }
  if (raw.kind === "callout") {
    const tail = member(raw, "tail", CALLOUT_TAILS, "bottom_left");
    const stroke = member(raw, "stroke_px", OUTLINE_PX, 1);
    const color = colorMember(raw, "color", grammar.color, "rule" as C);
    const fill = colorMember(raw, "fill", grammar.fill, "surface" as F);
    const size = member(raw, "text_size", CALLOUT_TEXT_SIZES, "md");
    const rawText = "text" in raw ? raw.text : "";
    if (tail === null || stroke === null || color === null || fill === null || size === null) return null;
    if (typeof rawText !== "string") return null;
    const text = rawText.replace(/\r\n?/g, "\n").replace(CALLOUT_STRIP_RE, "");
    if (text.length > CALLOUT_MAX_CHARS) return null;
    return { id, kind: "callout", placement, tail, stroke_px: stroke, color, fill, text, text_size: size, ...finish };
  }
  return null;
}

/** A stored backdrop, re-read for drawing: the valid shapes in stacking order.
 *
 *  Mirrors `parse_backdrop`: a shape outside the sets is dropped ON ITS OWN (a
 *  backdrop is decoration; one bad shape must not blank the other 49), a
 *  second shape with an id already seen is dropped, and nothing past the cap
 *  is drawn. Unlike a border, partial is right here: each shape is a whole
 *  drawing of its own, and the shapes that remain are ones the author chose.
 *
 *  Accepts the column value (`{"shapes": [...]}`) or the bare list, because
 *  the report transport carries a page's list without its wrapper. */
export function coerceBackdrop(raw: unknown): BackdropShape[] {
  return coerceBackdropWith(raw, REPORT_GRAMMAR);
}

/** A stored DASHBOARD backdrop: the same rules, the dashboard colour grammar
 *  (ADR-0380). A theme-slot or hex shape survives here and is dropped by
 *  `coerceBackdrop`. */
export function coerceDashboardBackdrop(raw: unknown): DashboardBackdropShape[] {
  return coerceBackdropWith(raw, DASHBOARD_GRAMMAR);
}

function coerceBackdropWith<C extends string, F extends string>(
  raw: unknown,
  grammar: ColorGrammar<C, F>,
): BackdropShape<C, F>[] {
  const list = Array.isArray(raw) ? raw : isRecord(raw) && Array.isArray(raw.shapes) ? raw.shapes : [];
  const shapes: BackdropShape<C, F>[] = [];
  const seen = new Set<string>();
  for (const entry of list) {
    if (shapes.length >= BACKDROP_MAX_SHAPES) break;
    const shape = coerceOneShape(entry, grammar);
    if (!shape || seen.has(shape.id)) continue;
    seen.add(shape.id);
    shapes.push(shape);
  }
  return shapes;
}
